import React, { useEffect, useMemo, useState } from 'react';
import PublicAnglerPreview from '../PublicAnglerPreview';
import type { Tournament, TournamentParticipant, UserProfile } from '../../types';
import {
  adminManageTournamentParticipant,
  createTournamentDuoRegistration,
  getMyTournamentDuo,
  joinTournamentDuoRegistration,
  joinTournamentRegistration,
  leaveTournamentRegistration,
  subscribeTournamentParticipants,
} from '../../services/tournaments';

type ToastFn = (message: string, type?: any) => void;

type Props = {
  tournament: Tournament;
  userProfile: UserProfile;
  canManageParticipants: boolean;
  registrationEnabled: boolean;
  addToast: ToastFn;
  compact?: boolean;
  onOpenUserProfile?: (userId: string, tournamentId?: string) => void;
};

type MyDuoInfo = {
  teamId: string;
  teamName: string;
  captainUid?: string | null;
  memberUids: string[];
  status: string;
  duoCode: string;
  complete: boolean;
};

const activeStatuses = new Set(['pending', 'approved']);

type DuoFieldGroup = {
  teamId: string;
  teamName: string;
  members: TournamentParticipant[];
  complete: boolean;
};

const participantAvatar = (participant?: TournamentParticipant | null) =>
  String((participant as any)?.avatarUrl || (participant as any)?.avatar || '').trim();

const participantTeamId = (participant?: TournamentParticipant | null) =>
  String((participant as any)?.teamId || '').trim();

const participantTeamName = (participant?: TournamentParticipant | null) =>
  String((participant as any)?.teamName || '').trim();

export default function TournamentParticipantsPanel({
  tournament,
  userProfile,
  canManageParticipants,
  registrationEnabled,
  addToast,
  compact = false,
  onOpenUserProfile,
}: Props) {
  const [participants, setParticipants] = useState<Record<string, TournamentParticipant>>({});
  const [busyKey, setBusyKey] = useState<string | null>(null);
  const [clockNow, setClockNow] = useState(() => Date.now());
  const [duoMode, setDuoMode] = useState<'create' | 'join'>('create');
  const [duoName, setDuoName] = useState('');
  const [duoCode, setDuoCode] = useState('');
  const [myDuo, setMyDuo] = useState<MyDuoInfo | null>(null);
  const [withdrawConfirmOpen, setWithdrawConfirmOpen] = useState(false);
  const [previewParticipant, setPreviewParticipant] = useState<TournamentParticipant | null>(null);

  useEffect(() => {
    const timer = window.setInterval(() => setClockNow(Date.now()), 15_000);
    return () => window.clearInterval(timer);
  }, []);

  useEffect(
    () => subscribeTournamentParticipants(tournament.id, setParticipants),
    [tournament.id],
  );

  const rows = useMemo(
    () =>
      Object.values(participants).sort(
        (a, b) => Number(a.joinedAt || 0) - Number(b.joinedAt || 0),
      ),
    [participants],
  );

  const approvedRows = useMemo(
    () => rows.filter((participant) => participant.status === 'approved'),
    [rows],
  );

  const pendingRows = useMemo(
    () => rows.filter((participant) => participant.status === 'pending'),
    [rows],
  );

  const visibleRows = canManageParticipants
    ? rows.filter((participant) => activeStatuses.has(participant.status))
    : approvedRows;

  const duoFieldGroups = useMemo<DuoFieldGroup[]>(() => {
    if (tournament.participationMode !== 'team') return [];

    const active = visibleRows.filter((participant) => activeStatuses.has(participant.status));
    const grouped = new Map<string, TournamentParticipant[]>();
    const unpaired: TournamentParticipant[] = [];

    active.forEach((participant) => {
      const teamId = participantTeamId(participant);
      if (!teamId) {
        unpaired.push(participant);
        return;
      }
      const bucket = grouped.get(teamId) || [];
      bucket.push(participant);
      grouped.set(teamId, bucket);
    });

    const groups: DuoFieldGroup[] = [...grouped.entries()].map(([teamId, members]) => {
      const sortedMembers = members.slice().sort((a, b) => Number(a.joinedAt || 0) - Number(b.joinedAt || 0));
      const teamName = sortedMembers.map(participantTeamName).find(Boolean) || 'Roddo Duo';
      return {
        teamId,
        teamName,
        members: sortedMembers.slice(0, 2),
        complete: sortedMembers.length >= 2 && sortedMembers.slice(0, 2).every((member) => member.status === 'approved'),
      };
    });

    unpaired.forEach((participant) => {
      groups.push({
        teamId: `unpaired:${participant.uid}`,
        teamName: participantTeamName(participant) || 'Forming Duo',
        members: [participant],
        complete: false,
      });
    });

    return groups.sort((a, b) => {
      if (a.complete !== b.complete) return a.complete ? -1 : 1;
      return a.teamName.localeCompare(b.teamName);
    });
  }, [tournament.participationMode, visibleRows]);

  const mine = participants[userProfile.uid];
  const isDuoTournament = tournament.participationMode === 'team';
  const registrationClosesAt = Number(tournament.registrationClosesAt || 0);
  const registrationDeadlinePassed =
    registrationClosesAt > 0 && registrationClosesAt <= clockNow;
  const registrationOpen =
    registrationEnabled &&
    tournament.status === 'registration_open' &&
    !registrationDeadlinePassed;
  const activeRegistration = !!mine && activeStatuses.has(mine.status);
  const canWithdraw =
    activeRegistration &&
    ['approved', 'registration_open'].includes(String(tournament.status || ''));

  const duoRows = useMemo(() => {
    const teamId = String((mine as any)?.teamId || '').trim();
    if (!teamId) return [];
    return rows.filter(
      (participant) =>
        activeStatuses.has(participant.status) &&
        String((participant as any)?.teamId || '').trim() === teamId,
    );
  }, [mine, rows]);

  useEffect(() => {
    let cancelled = false;
    if (!isDuoTournament || !activeRegistration || !(mine as any)?.teamId) {
      setMyDuo(null);
      return;
    }

    void getMyTournamentDuo(tournament.id)
      .then((result) => {
        if (!cancelled) setMyDuo(result as MyDuoInfo);
      })
      .catch((error) => {
        if (!cancelled) {
          setMyDuo(null);
          console.warn('[TournamentParticipantsPanel] duo lookup failed', error);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [activeRegistration, isDuoTournament, (mine as any)?.teamId, tournament.id]);

  const displayName =
    userProfile.name || userProfile.displayName || 'Roddo Angler';
  const avatarUrl =
    userProfile.avatarUrl || userProfile.avatar || userProfile.photoURL || null;

  const joinSolo = async () => {
    setBusyKey('join');
    try {
      const status = await joinTournamentRegistration(
        tournament.id,
        displayName,
        avatarUrl,
      );
      addToast(
        status === 'pending'
          ? 'Registration request sent for approval.'
          : 'You are registered for this tournament.',
        'success',
      );
    } catch (error: any) {
      addToast(error?.message || 'Could not register.', 'error');
    } finally {
      setBusyKey(null);
    }
  };

  const joinDuo = async () => {
    const cleanName = duoName.trim();
    const cleanCode = duoCode.trim().toUpperCase().replace(/[^A-Z0-9]/g, '');

    if (duoMode === 'create' && cleanName.length < 2) {
      addToast('Give your Duo a name first.', 'info');
      return;
    }
    if (duoMode === 'join' && cleanCode.length < 4) {
      addToast('Enter the Duo code from your partner.', 'info');
      return;
    }

    setBusyKey(`duo:${duoMode}`);
    try {
      const result = duoMode === 'create'
        ? await createTournamentDuoRegistration(
            tournament.id,
            displayName,
            avatarUrl,
            cleanName,
          )
        : await joinTournamentDuoRegistration(
            tournament.id,
            displayName,
            avatarUrl,
            cleanCode,
          );

      setMyDuo({
        teamId: String(result.teamId || ''),
        teamName: String(result.teamName || cleanName || 'Roddo Duo'),
        memberUids: [userProfile.uid],
        status: result.duoComplete ? 'active' : 'forming',
        duoCode: String(result.duoCode || cleanCode),
        complete: result.duoComplete === true,
      });
      addToast(
        result.status === 'pending'
          ? 'Duo registration sent for approval.'
          : result.duoComplete
            ? 'Duo linked. Both anglers are registered.'
            : 'Duo created. Share the Duo code with your partner.',
        'success',
      );
    } catch (error: any) {
      addToast(error?.message || 'Could not register this Duo.', 'error');
    } finally {
      setBusyKey(null);
    }
  };

  const leave = async () => {
    setBusyKey('leave');
    try {
      await leaveTournamentRegistration(tournament.id);
      setMyDuo(null);
      setWithdrawConfirmOpen(false);
      addToast('Tournament registration withdrawn.', 'info');
    } catch (error: any) {
      addToast(error?.message || 'Could not withdraw.', 'error');
    } finally {
      setBusyKey(null);
    }
  };

  const manage = async (
    participant: TournamentParticipant,
    action: 'approve' | 'remove',
  ) => {
    const reason =
      action === 'remove'
        ? window.prompt(`Why are you removing ${participant.displayName}?`, '') || ''
        : '';

    if (action === 'remove' && reason.trim().length < 3) return;

    setBusyKey(`${action}:${participant.uid}`);
    try {
      await adminManageTournamentParticipant(
        tournament.id,
        participant.uid,
        action,
        reason,
      );
      addToast(action === 'approve' ? 'Participant approved.' : 'Participant removed.', 'success');
    } catch (error: any) {
      addToast(error?.message || 'Participant action failed.', 'error');
    } finally {
      setBusyKey(null);
    }
  };

  const copyDuoCode = async () => {
    const code = String(myDuo?.duoCode || '').trim();
    if (!code) return;
    try {
      await navigator.clipboard.writeText(code);
      addToast('Duo code copied.', 'success');
    } catch {
      addToast(`Duo code: ${code}`, 'info');
    }
  };

  return (
    <div className={compact ? '' : 'space-y-4'}>
      {!compact ? (
        <div className="flex items-start justify-between gap-3">
          <div>
            <h3 className="text-base font-black">Participants</h3>
            <p className="mt-1 text-xs font-semibold opacity-60">
              {approvedRows.length} approved
              {canManageParticipants ? ` · ${pendingRows.length} pending` : ''}
              {tournament.maxParticipants ? ` · ${tournament.maxParticipants} max` : ''}
            </p>
          </div>
          {mine ? (
            <span className="rounded-full bg-white/[.05] px-2.5 py-1 text-[10px] font-black uppercase tracking-[.1em] opacity-75">
              {mine.status.replaceAll('_', ' ')}
            </span>
          ) : null}
        </div>
      ) : null}

      {registrationOpen && !activeRegistration && isDuoTournament ? (
        <div className="rounded-[14px] border border-amber-400/20 bg-amber-400/[.06] p-3">
          <div className="text-sm font-black">Duo registration</div>
          <p className="mt-1 text-[11px] font-semibold leading-relaxed opacity-60">
            Every Duo is locked to exactly two Roddo accounts. Create one and share the code, or join your partner's Duo.
          </p>

          <div className="mt-3 grid grid-cols-2 gap-1 rounded-[11px] bg-black/[.035] p-1 dark:bg-white/[.045]">
            <button
              type="button"
              onClick={() => setDuoMode('create')}
              className={`rounded-[9px] px-3 py-2 text-xs font-black ${duoMode === 'create' ? 'bg-[#2F80ED] text-white dark:bg-[#7057E8]' : 'opacity-55'}`}
            >
              Create Duo
            </button>
            <button
              type="button"
              onClick={() => setDuoMode('join')}
              className={`rounded-[9px] px-3 py-2 text-xs font-black ${duoMode === 'join' ? 'bg-[#2F80ED] text-white dark:bg-[#7057E8]' : 'opacity-55'}`}
            >
              Join Duo
            </button>
          </div>

          {duoMode === 'create' ? (
            <input
              value={duoName}
              onChange={(event) => setDuoName(event.currentTarget.value)}
              maxLength={80}
              placeholder="Duo name"
              className="mt-3 w-full rounded-[11px] border border-black/10 bg-white/70 px-3 py-2.5 text-sm font-bold outline-none dark:border-white/[.10] dark:bg-white/[.05]"
            />
          ) : (
            <input
              value={duoCode}
              onChange={(event) => setDuoCode(event.currentTarget.value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 10))}
              maxLength={10}
              autoCapitalize="characters"
              placeholder="Duo code"
              className="mt-3 w-full rounded-[11px] border border-black/10 bg-white/70 px-3 py-2.5 font-mono text-sm font-black uppercase tracking-[.16em] outline-none dark:border-white/[.10] dark:bg-white/[.05]"
            />
          )}

          <button
            type="button"
            onClick={() => void joinDuo()}
            disabled={busyKey !== null}
            className="mt-3 w-full rounded-[13px] bg-[linear-gradient(180deg,#FFD96A_0%,#FFC94A_55%,#F2B53A_100%)] px-4 py-3 text-sm font-black text-[#2F2410] shadow-[0_8px_22px_rgba(181,126,24,.16)] disabled:opacity-45"
          >
            {busyKey?.startsWith('duo:')
              ? 'Registering…'
              : duoMode === 'create'
                ? 'Create Duo & register'
                : 'Join Duo & register'}
          </button>
        </div>
      ) : registrationOpen && !activeRegistration ? (
        <button
          type="button"
          onClick={() => void joinSolo()}
          disabled={busyKey !== null}
          className="w-full rounded-[14px] bg-[linear-gradient(180deg,#FFD96A_0%,#FFC94A_55%,#F2B53A_100%)] px-4 py-3 text-sm font-black text-[#2F2410] shadow-[0_8px_22px_rgba(181,126,24,.16)] disabled:opacity-45"
        >
          {busyKey === 'join'
            ? 'Registering…'
            : tournament.participantApprovalRequired
              ? 'Request registration'
              : 'Join tournament'}
        </button>
      ) : null}

      {activeRegistration ? (
        <div className="rounded-[14px] bg-white/[.045] px-3 py-3">
          <div className="flex items-center justify-between gap-3">
            <div className="min-w-0">
              <div className="font-black">
                {mine?.status === 'approved' ? 'You are registered' : 'Awaiting approval'}
              </div>
              <div className="mt-0.5 text-xs font-semibold opacity-55">
                {mine?.status === 'approved'
                  ? isDuoTournament
                    ? myDuo?.complete
                      ? 'Your team is locked and ready.'
                      : 'Your team is waiting for a second linked angler.'
                    : 'You are cleared to compete when the event is live.'
                  : 'The tournament director will review your request.'}
              </div>
            </div>
            {canWithdraw ? (
              <button
                type="button"
                onClick={() => setWithdrawConfirmOpen(true)}
                disabled={busyKey !== null}
                className="shrink-0 rounded-[11px] px-3 py-2 text-xs font-black text-[#EF8B91] hover:bg-red-500/10 disabled:opacity-45"
              >
                {busyKey === 'leave' ? 'Withdrawing…' : 'Withdraw'}
              </button>
            ) : null}
          </div>

        </div>
      ) : null}

      {isDuoTournament ? (
          <div className={`${compact && activeRegistration ? 'mt-4' : ''} space-y-3`}>
            <div className="flex items-end justify-between gap-3 px-0.5">
              <div>
                <div className="text-[10px] font-black uppercase tracking-[.14em] opacity-45">Tournament field</div>
                <div className="mt-0.5 text-sm font-black">{duoFieldGroups.length} Duo{duoFieldGroups.length === 1 ? '' : 's'} · {visibleRows.length} anglers</div>
              </div>
              <div className="text-[10px] font-black uppercase tracking-[.1em] opacity-40">Team field</div>
            </div>

            {duoFieldGroups.length ? (
              duoFieldGroups.map((group, index) => {
                const isMyTeam = group.members.some((member) => member.uid === userProfile.uid);
                const first = group.members[0];
                const second = group.members[1];
                return (
                  <div
                    key={group.teamId}
                    className={`overflow-hidden rounded-[16px] border ${isMyTeam ? 'border-[#2F80ED]/24 bg-[#2F80ED]/[.055] dark:border-[#7057E8]/26 dark:bg-[#7057E8]/[.08]' : 'border-black/[.07] bg-white/[.035] dark:border-white/[.08] dark:bg-white/[.035]'}`}
                  >
                    <div className="flex items-center justify-between gap-3 border-b border-black/[.06] px-3 py-2.5 dark:border-white/[.07]">
                      <div className="min-w-0">
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="text-[9px] font-black uppercase tracking-[.13em] opacity-40">Team {String(index + 1).padStart(2, '0')}</span>
                          {isMyTeam ? (
                            <span className="rounded-full bg-[#2F80ED]/10 px-2 py-0.5 text-[8px] font-black uppercase tracking-[.09em] text-[#2168C9] dark:bg-[#7057E8]/15 dark:text-[#CFC7FF]">Your Team</span>
                          ) : null}
                        </div>
                        <div className="mt-1 truncate text-sm font-black">{group.teamName}</div>
                      </div>
                      <span className={`shrink-0 rounded-full px-2.5 py-1 text-[9px] font-black uppercase tracking-[.09em] ${group.complete ? 'bg-emerald-400/12 text-emerald-700 dark:text-emerald-200' : 'bg-amber-400/12 text-amber-800 dark:text-amber-200'}`}>
                        {group.complete ? 'Ready · 2/2' : `${group.members.length}/2 · Needs partner`}
                      </span>
                    </div>

                    <div className="grid grid-cols-[1fr_auto_1fr] items-stretch gap-2 px-3 py-3">
                      {[first, second].map((member, memberIndex) => (
                        <React.Fragment key={member?.uid || `open-${memberIndex}`}>
                          {memberIndex === 1 ? (
                            <div className="flex items-center justify-center px-0.5">
                              <span className="grid h-8 w-8 place-items-center rounded-full border border-black/[.07] bg-black/[.035] text-sm dark:border-white/[.08] dark:bg-white/[.05]">🤝</span>
                            </div>
                          ) : null}
                          <div
                            className={`relative min-w-0 rounded-[13px] border px-2.5 py-2.5 text-center ${member ? 'cursor-pointer border-black/[.06] bg-black/[.025] transition hover:bg-black/[.045] active:scale-[.99] dark:border-white/[.07] dark:bg-white/[.035] dark:hover:bg-white/[.055]' : 'border-dashed border-black/[.10] bg-transparent opacity-55 dark:border-white/[.12]'}`}
                            role={member ? 'button' : undefined}
                            tabIndex={member ? 0 : undefined}
                            aria-label={member ? `View ${member.displayName || 'angler'} profile preview` : undefined}
                            onClick={() => { if (member) setPreviewParticipant(member); }}
                            onKeyDown={(event) => {
                              if (!member) return;
                              if (event.key === 'Enter' || event.key === ' ') {
                                event.preventDefault();
                                setPreviewParticipant(member);
                              }
                            }}
                          >
                            {member ? (
                              <>
                                {member.status === 'approved' ? (
                                  <span
                                    className="absolute right-2 top-2 grid h-5 w-5 place-items-center rounded-full bg-emerald-500 text-[11px] font-black text-white shadow-[0_3px_8px_rgba(16,185,129,.22)]"
                                    aria-label="Approved angler"
                                    title="Approved"
                                  >
                                    ✓
                                  </span>
                                ) : null}
                                {participantAvatar(member) ? (
                                  <img src={participantAvatar(member)} alt="" className="mx-auto h-11 w-11 rounded-full object-cover" />
                                ) : (
                                  <div className="mx-auto grid h-11 w-11 place-items-center rounded-full bg-[#2F80ED]/10 text-sm font-black text-[#2168C9] dark:bg-[#7057E8]/15 dark:text-violet-200">
                                    {(member.displayName || 'R').slice(0, 1).toUpperCase()}
                                  </div>
                                )}
                                <div className="mt-2 truncate text-[12px] font-black">{member.displayName}</div>
                                <div className="mt-0.5 text-[9px] font-black uppercase tracking-[.08em] opacity-45">
                                  {memberIndex === 0 ? 'Captain' : 'Partner'}
                                </div>
                              </>
                            ) : (
                              <>
                                <div className="mx-auto grid h-11 w-11 place-items-center rounded-full border border-dashed border-current text-lg opacity-45">+</div>
                                <div className="mt-2 text-[12px] font-black">Open seat</div>
                                <div className="mt-0.5 text-[9px] font-black uppercase tracking-[.08em] opacity-45">Waiting for partner</div>
                              </>
                            )}
                          </div>
                        </React.Fragment>
                      ))}
                    </div>

                    {canManageParticipants ? (
                      <div className="flex flex-wrap justify-end gap-1.5 border-t border-black/[.06] px-3 py-2.5 dark:border-white/[.07]">
                        {group.members.map((participant) => (
                          <React.Fragment key={participant.uid}>
                            {participant.status === 'pending' ? (
                              <button type="button" onClick={() => void manage(participant, 'approve')} disabled={busyKey !== null} className="rounded-[9px] bg-emerald-400/10 px-2.5 py-1.5 text-[10px] font-black text-emerald-700 dark:text-emerald-200 disabled:opacity-45">
                                Approve {participant.displayName}
                              </button>
                            ) : null}
                            {activeStatuses.has(participant.status) ? (
                              <button type="button" onClick={() => void manage(participant, 'remove')} disabled={busyKey !== null} className="rounded-[9px] px-2.5 py-1.5 text-[10px] font-black text-[#EF8B91] hover:bg-red-500/10 disabled:opacity-45">
                                Remove {participant.displayName}
                              </button>
                            ) : null}
                          </React.Fragment>
                        ))}
                      </div>
                    ) : null}
                  </div>
                );
              })
            ) : (
              <p className="py-2 text-sm font-semibold opacity-55">No approved Duos yet.</p>
            )}
          </div>
        ) : (
          <div className={`${compact && activeRegistration ? 'mt-3.5' : ''} space-y-2`}>
            {visibleRows.length ? (
              visibleRows.map((participant) => (
                <div key={participant.uid} className="flex items-center justify-between gap-3 rounded-[14px] bg-white/[.035] px-3 py-2.5">
                  <button
                    type="button"
                    onClick={() => setPreviewParticipant(participant)}
                    className="flex min-w-0 flex-1 items-center gap-2.5 rounded-[10px] text-left transition hover:bg-black/[.025] active:scale-[.995] dark:hover:bg-white/[.035]"
                    aria-label={`View ${participant.displayName || 'angler'} profile preview`}
                  >
                    {participant.avatarUrl ? (
                      <img src={participant.avatarUrl} alt="" className="h-9 w-9 shrink-0 rounded-full object-cover" />
                    ) : (
                      <div className="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-[#2F80ED]/12 text-xs font-black text-[#2168C9] dark:bg-[#7057E8]/15 dark:text-violet-200">
                        {(participant.displayName || 'R').slice(0, 1).toUpperCase()}
                      </div>
                    )}
                    <div className="min-w-0">
                      <div className="truncate text-sm font-black">{participant.displayName}</div>
                      <div className="text-[11px] font-semibold opacity-50">{participant.status.replaceAll('_', ' ')}</div>
                    </div>
                  </button>
                  {canManageParticipants ? (
                    <div className="flex shrink-0 items-center gap-1.5">
                      {participant.status === 'pending' ? (
                        <button type="button" onClick={() => void manage(participant, 'approve')} disabled={busyKey !== null} className="rounded-[10px] bg-emerald-400/10 px-2.5 py-2 text-[11px] font-black text-emerald-700 dark:text-emerald-200 disabled:opacity-45">Approve</button>
                      ) : null}
                      {activeStatuses.has(participant.status) ? (
                        <button type="button" onClick={() => void manage(participant, 'remove')} disabled={busyKey !== null} className="rounded-[10px] px-2.5 py-2 text-[11px] font-black text-[#EF8B91] hover:bg-red-500/10 disabled:opacity-45">Remove</button>
                      ) : null}
                    </div>
                  ) : null}
                </div>
              ))
            ) : (
              <p className="py-2 text-sm font-semibold opacity-55">No approved contestants yet.</p>
            )}
          </div>
        )}

      {previewParticipant ? (
        <PublicAnglerPreview
          userId={previewParticipant.uid}
          fallbackName={previewParticipant.displayName}
          fallbackAvatar={participantAvatar(previewParticipant) || null}
          onClose={() => setPreviewParticipant(null)}
          onViewProfile={(userId) => onOpenUserProfile?.(userId, tournament.id)}
        />
      ) : null}

      {withdrawConfirmOpen ? (
        <div
          className="fixed inset-0 z-[1200] grid place-items-center bg-black/65 p-4 backdrop-blur-[2px]"
          role="presentation"
          onClick={() => { if (busyKey !== 'leave') setWithdrawConfirmOpen(false); }}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="tournament-withdraw-title"
            className="w-full max-w-sm rounded-[20px] border border-black/10 bg-[#FFF9EF] p-4 shadow-[0_22px_70px_rgba(0,0,0,.28)] dark:border-white/[.10] dark:bg-[#111A2B]"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="flex items-start gap-3">
              <div className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-red-500/10 text-lg">⚠️</div>
              <div className="min-w-0">
                <div id="tournament-withdraw-title" className="text-[17px] font-black">Withdraw from tournament?</div>
                <p className="mt-1 text-xs font-semibold leading-5 opacity-65">
                  This removes your tournament registration. {isDuoTournament ? 'Your Duo may no longer be complete. ' : ''}You can only re-register while registration is still open and space is available.
                </p>
              </div>
            </div>

            <div className="mt-4 grid grid-cols-2 gap-2">
              <button
                type="button"
                onClick={() => setWithdrawConfirmOpen(false)}
                disabled={busyKey === 'leave'}
                className="rounded-[13px] border border-black/10 px-3 py-3 text-xs font-black dark:border-white/[.10] disabled:opacity-45"
              >
                Keep my spot
              </button>
              <button
                type="button"
                onClick={() => void leave()}
                disabled={busyKey !== null}
                className="rounded-[13px] bg-red-500 px-3 py-3 text-xs font-black text-white shadow-[0_7px_18px_rgba(239,68,68,.18)] disabled:opacity-45"
              >
                {busyKey === 'leave' ? 'Withdrawing…' : 'Withdraw'}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
